import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, XCircle } from 'lucide-react';
import { Alert, Badge, Button, Spinner, Switch, TextField } from '../components/ui';
import { Wordmark } from '../components/brand/Wordmark.jsx';
import { resolveSetupStage } from '../utils/setupFlow.js';
import { useAuth } from '../context/AuthContext';

function authFetch(url, token, options = {}) {
  return fetch(url, {
    ...options,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...options.headers }
  });
}

const STEP_IDS = ['admin', 'sources', 'done'];

/**
 * First-run wizard (spec §10.1).
 *
 * The step sequence and every request it makes are unchanged from the pre-Etch
 * version — this is a re-skin plus the three things the spec asks for that were
 * missing: a step indicator that is announced, per-field validation that leaves
 * the user's input intact, and a connection test whose result is a state rather
 * than a string spliced into the layout.
 */
export default function SetupPage() {
  const { t } = useTranslation();
  const { token: authToken, setSession } = useAuth();

  const [step, setStep] = useState(1);
  const [setupToken, setSetupToken] = useState('');
  const [setupStatus, setSetupStatus] = useState(null);
  const [statusError, setStatusError] = useState('');
  const [oidc, setOidc] = useState(null);
  // Separate from `oidc`, which is null both while loading and when disabled.
  // Branching before this is true would flash the standalone admin form on a
  // hosted instance — and, worse, fire the standalone step-2 jump.
  const [oidcChecked, setOidcChecked] = useState(false);
  const [claimTried, setClaimTried] = useState(false);
  const [claimLoading, setClaimLoading] = useState(false);
  const [claimError, setClaimError] = useState('');
  const [deps, setDeps] = useState(null);
  const [pipInstalling, setPipInstalling] = useState(false);
  const [pipOutput, setPipOutput] = useState('');

  const [adminForm, setAdminForm] = useState({ username: '', email: '', password: '', confirm: '' });
  const [adminFieldErrors, setAdminFieldErrors] = useState({});
  const [adminError, setAdminError] = useState('');
  const [adminLoading, setAdminLoading] = useState(false);

  const [sources, setSources] = useState({
    documents: { enabled: false, url: 'http://localhost:8082' },
    axiom: { enabled: false, token: '' },
    repos: { enabled: false }
  });
  const [testResults, setTestResults] = useState({});
  const [testLoading, setTestLoading] = useState({});
  const [sourcesError, setSourcesError] = useState('');
  const [sourcesLoading, setSourcesLoading] = useState(false);

  const [confirmReset, setConfirmReset] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [resetError, setResetError] = useState('');

  useEffect(() => {
    fetch('/api/setup/deps')
      .then((r) => r.json())
      .then(setDeps)
      .catch(() => setDeps(null));

    fetch('/api/auth/oidc/status')
      .then((r) => r.json())
      .then((data) => setOidc(data?.enabled ? data : null))
      .catch(() => setOidc(null))
      .finally(() => setOidcChecked(true));

    fetch('/api/setup/status')
      .then((r) => r.json())
      .then((data) => {
        setSetupStatus({ initialized: !!data.initialized, adminExists: !!data.adminExists });
      })
      .catch(() => {
        setStatusError(t('setupWizard.statusUnreachable'));
        setSetupStatus(null);
      });
  }, [t]);

  /**
   * The whole first-run decision, in one place — see setupFlow.js for why it
   * is not the three-boolean check it looks like.
   */
  const stage = useMemo(
    () => resolveSetupStage({
      oidcEnabled: Boolean(oidc?.enabled),
      statusKnown: oidcChecked && setupStatus !== null,
      initialized: setupStatus?.initialized,
      adminExists: setupStatus?.adminExists,
      hasToken: Boolean(authToken),
    }),
    [oidc, oidcChecked, setupStatus, authToken]
  );

  const statusChecked = stage.stage !== 'unknown' || Boolean(statusError);

  useEffect(() => {
    if (stage.stage === 'redirect') {
      window.location.replace(stage.to);
      return;
    }
    // Standalone only: a half-finished install with a session resumes at
    // sources. The hosted path must not take this shortcut — it has to claim
    // the instance first, or `setup.initialized` never gets set and the API
    // gate that keys off it never switches on.
    if (stage.stage === 'sources' && authToken) {
      setSetupToken(authToken);
      setStep((current) => (current === 1 ? 2 : current));
    }
  }, [stage, authToken]);

  /**
   * Hosted first run: claiming the instance is its own call, because the OIDC
   * callback deliberately does not decide who owns the box — it only signs the
   * user in. Runs once the callback has handed AuthContext a token.
   *
   * A 409 means someone claimed it in between; that is a race, not a failure,
   * so the wizard simply carries on to step 2.
   */
  useEffect(() => {
    if (stage.stage !== 'claim' || claimTried) return;
    setClaimTried(true);
    setClaimLoading(true);
    fetch('/api/setup/claim', {
      method: 'POST',
      headers: { Authorization: `Bearer ${authToken}` }
    })
      .then(async (res) => {
        const data = await res.json().catch(() => ({}));
        if (res.ok || res.status === 409) {
          setSetupToken(authToken);
          setStep(2);
          return;
        }
        if (data.error === 'claim_requires_operator') {
          setClaimError(t('setupWizard.errorClaimOperator', { tenant: data.tenant_slug || '—' }));
        } else if (data.error === 'claim_requires_platform_identity') {
          setClaimError(t('setupWizard.errorClaimIdentity'));
        } else {
          setClaimError(t('setupWizard.errorClaimFailed'));
        }
      })
      .catch(() => setClaimError(t('setupWizard.errorClaimFailed')))
      .finally(() => setClaimLoading(false));
  }, [stage, authToken, claimTried, t]);

  async function handleAdminSubmit(event) {
    event.preventDefault();
    setAdminError('');

    const fieldErrors = {};
    if (!adminForm.username.trim()) fieldErrors.username = t('setupWizard.errorUsernameRequired');
    if (adminForm.password.length < 8) fieldErrors.password = t('auth.error.passwordTooShort');
    if (adminForm.password !== adminForm.confirm) fieldErrors.confirm = t('auth.error.passwordMismatch');
    setAdminFieldErrors(fieldErrors);
    if (Object.keys(fieldErrors).length > 0) return;

    setAdminLoading(true);
    try {
      const res = await fetch('/api/setup/init', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: adminForm.username.trim(),
          email: adminForm.email.trim() || undefined,
          password: adminForm.password
        })
      });
      if (!res.ok) throw new Error(String(res.status));
      const data = await res.json();
      setSetupToken(data.token);
      setSession(data.token, data.user);
      setStep(2);
    } catch {
      // The user's input stays in the form — retrying must never mean retyping.
      setAdminError(t('setupWizard.errorAdminCreate'));
    } finally {
      setAdminLoading(false);
    }
  }

  async function handleTestConnection(type) {
    setTestLoading((s) => ({ ...s, [type]: true }));
    setTestResults((s) => ({ ...s, [type]: null }));
    try {
      const payload =
        type === 'documents' ? { url: sources.documents.url } : type === 'axiom' ? { token: sources.axiom.token } : {};
      const res = await authFetch(`/api/admin/sources/${type}/test`, setupToken, {
        method: 'POST',
        body: JSON.stringify(payload)
      });
      setTestResults((s) => ({ ...s, [type]: res.ok ? 'ok' : 'fail' }));
    } catch {
      setTestResults((s) => ({ ...s, [type]: 'fail' }));
    } finally {
      setTestLoading((s) => ({ ...s, [type]: false }));
    }
  }

  async function handleSourcesSubmit(event) {
    event.preventDefault();
    setSourcesError('');
    setSourcesLoading(true);
    try {
      const res = await authFetch('/api/setup/sources', setupToken, {
        method: 'POST',
        body: JSON.stringify({
          documents: { enabled: sources.documents.enabled, url: sources.documents.url },
          axiom: { enabled: sources.axiom.enabled, token: sources.axiom.token },
          repos: { enabled: sources.repos.enabled }
        })
      });
      if (!res.ok) throw new Error(String(res.status));
      setStep(3);
    } catch {
      setSourcesError(t('setupWizard.errorSourcesSave'));
    } finally {
      setSourcesLoading(false);
    }
  }

  async function handlePipInstall() {
    setPipInstalling(true);
    setPipOutput('');
    try {
      const res = await fetch('/api/admin/sources/documents/pip-install', {
        method: 'POST',
        headers: { Authorization: `Bearer ${setupToken}` }
      });
      const data = await res.json();
      setPipOutput(data.output || '');
      if (data.ok) setDeps((d) => ({ ...d, ingestible: true }));
    } catch {
      setPipOutput(t('setupWizard.errorPipInstall'));
    } finally {
      setPipInstalling(false);
    }
  }

  async function handleReset() {
    setResetLoading(true);
    setResetError('');
    try {
      const res = await fetch('/api/setup/reset', { method: 'POST' });
      if (!res.ok) throw new Error(String(res.status));
      setAdminExists(false);
      setConfirmReset(false);
    } catch {
      setResetError(t('setupWizard.errorReset'));
    } finally {
      setResetLoading(false);
    }
  }

  function updateSource(type, field, value) {
    setSources((s) => ({ ...s, [type]: { ...s[type], [field]: value } }));
    // Any edit invalidates the previous verdict; a stale green tick is worse
    // than no tick at all.
    setTestResults((r) => ({ ...r, [type]: null }));
  }

  if (!statusChecked) {
    return (
      <div className="etch-auth-screen" role="status" aria-live="polite">
        <Spinner size="lg" label={t('states.loading')} />
      </div>
    );
  }

  const stepLabels = {
    admin: t('setupWizard.stepAdmin'),
    sources: t('setupWizard.stepSources'),
    done: t('setupWizard.stepDone')
  };

  const enabledCount = Object.values(sources).filter((s) => s.enabled).length;

  function TestResult({ type }) {
    const result = testResults[type];
    if (testLoading[type]) {
      return (
        <p role="status" aria-live="polite" className="etch-meta">
          {t('setupWizard.testRunning')}
        </p>
      );
    }
    if (!result) return null;
    return (
      <p role="status" aria-live="polite">
        <Badge tone={result === 'ok' ? 'success' : 'danger'}>
          {result === 'ok' ? <CheckCircle2 size={14} aria-hidden="true" /> : <XCircle size={14} aria-hidden="true" />}
          {result === 'ok' ? t('setupWizard.testOk') : t('setupWizard.testFailed')}
        </Badge>
      </p>
    );
  }

  return (
    <div className="etch-auth-screen">
      <div className="etch-auth-screen__card" data-width="wide">
        <header className="etch-auth-screen__brand">
          <Wordmark size="lg" />
          <p className="etch-auth-screen__tagline">{t('auth.setupIntro')}</p>
        </header>

        {/* Visible, announced step indicator (spec §10.1). */}
        <ol className="etch-setup-stepper" aria-label={t('setupWizard.progressLabel')}>
          {STEP_IDS.map((id, index) => {
            const number = index + 1;
            const state = number < step ? 'done' : number === step ? 'current' : 'todo';
            return (
              <li key={id} className="etch-setup-step" data-state={state} aria-current={state === 'current' || undefined}>
                <span className="etch-setup-step__marker" aria-hidden="true">
                  {state === 'done' ? <CheckCircle2 size={14} /> : number}
                </span>
                <span className="etch-setup-step__label">{stepLabels[id]}</span>
              </li>
            );
          })}
        </ol>
        <p className="etch-visually-hidden" role="status" aria-live="polite">
          {t('setupWizard.progressAnnounce', { step, total: STEP_IDS.length, label: stepLabels[STEP_IDS[step - 1]] })}
        </p>

        {statusError && (
          <Alert tone="warning" title={t('states.offline')}>
            {statusError}
          </Alert>
        )}

        {/* ---- Step 1a: an admin already exists (partial setup) ------------- */}
        {/* Standalone only. On a hosted instance an existing account proves
            nothing about ownership, and the reset button below would offer to
            delete real nyxCore users — which the server now refuses anyway. */}
        {step === 1 && stage.stage === 'partial' && (
          <section className="etch-auth-screen__form" aria-labelledby="setup-exists-heading">
            <h1 id="setup-exists-heading" className="etch-auth-screen__title">
              {t('setupWizard.adminExistsTitle')}
            </h1>
            <p className="etch-auth-screen__desc">{t('setupWizard.adminExistsDesc')}</p>

            <Link className="etch-button" data-variant="primary" data-block="true" to="/login">
              {t('setupWizard.signInToContinue')}
            </Link>

            {!confirmReset ? (
              <Button variant="ghost" block onClick={() => setConfirmReset(true)}>
                {t('setupWizard.resetStart')}
              </Button>
            ) : (
              <div className="etch-stack" data-gap="3">
                <Alert tone="danger" title={t('setupWizard.resetConfirmTitle')}>
                  {t('setupWizard.resetConfirmBody')}
                </Alert>
                {resetError && <Alert tone="danger">{resetError}</Alert>}
                <div className="etch-row">
                  <Button variant="danger" loading={resetLoading} disabled={resetLoading} onClick={handleReset}>
                    {t('setupWizard.resetConfirmAction')}
                  </Button>
                  <Button
                    variant="ghost"
                    disabled={resetLoading}
                    onClick={() => {
                      setConfirmReset(false);
                      setResetError('');
                    }}
                  >
                    {t('auth.cancel')}
                  </Button>
                </div>
              </div>
            )}
          </section>
        )}

        {/* ---- Step 1b: create the first admin ----------------------------- */}
        {/* ---- Step 1 (hosted): claim the instance with a nyxCore identity -- */}
        {step === 1 && (stage.stage === 'claim' || stage.stage === 'claim-signin') && (
          <section className="etch-auth-screen__form" aria-labelledby="setup-oidc-heading">
            <h1 id="setup-oidc-heading" className="etch-auth-screen__title">
              {t('setupWizard.oidcTitle')}
            </h1>
            <p className="etch-auth-screen__desc">{t('setupWizard.oidcDesc')}</p>

            {claimError && <Alert tone="danger">{claimError}</Alert>}

            {claimLoading ? (
              <p className="etch-auth-screen__desc" role="status">{t('setupWizard.claimPending')}</p>
            ) : (
              /* A real navigation, not fetch(): the OIDC flow leaves the SPA. */
              <a
                className="etch-button"
                data-variant="primary"
                data-block="true"
                href={`/api/auth/oidc/login?next=${encodeURIComponent('/setup')}`}
              >
                {t('auth.oidcButton')}
              </a>
            )}
          </section>
        )}

        {/* ---- Step 1 (standalone): local administrator account ------------- */}
        {step === 1 && stage.stage === 'local-admin' && (
          <form className="etch-auth-screen__form" onSubmit={handleAdminSubmit} noValidate>
            <h1 className="etch-auth-screen__title">{t('setupWizard.adminTitle')}</h1>
            <p className="etch-auth-screen__desc">{t('setupWizard.adminDesc')}</p>

            <TextField
              label={t('auth.username')}
              required
              autoComplete="username"
              autoCapitalize="none"
              spellCheck="false"
              error={adminFieldErrors.username}
              value={adminForm.username}
              onChange={(e) => setAdminForm((f) => ({ ...f, username: e.target.value }))}
            />
            <TextField
              label={t('auth.emailOptional')}
              type="email"
              autoComplete="email"
              value={adminForm.email}
              onChange={(e) => setAdminForm((f) => ({ ...f, email: e.target.value }))}
            />
            <TextField
              label={t('auth.password')}
              type="password"
              autoComplete="new-password"
              required
              hint={t('auth.passwordHint')}
              error={adminFieldErrors.password}
              value={adminForm.password}
              onChange={(e) => setAdminForm((f) => ({ ...f, password: e.target.value }))}
            />
            <TextField
              label={t('auth.passwordConfirm')}
              type="password"
              autoComplete="new-password"
              required
              error={adminFieldErrors.confirm}
              value={adminForm.confirm}
              onChange={(e) => setAdminForm((f) => ({ ...f, confirm: e.target.value }))}
            />

            {adminError && (
              <Alert tone="danger" title={t('states.error')}>
                {adminError}
              </Alert>
            )}

            <Button type="submit" block loading={adminLoading} disabled={adminLoading}>
              {t('setupWizard.continue')}
            </Button>
          </form>
        )}

        {/* ---- Step 2: knowledge sources ----------------------------------- */}
        {step === 2 && (
          <form className="etch-auth-screen__form" onSubmit={handleSourcesSubmit} noValidate>
            <h1 className="etch-auth-screen__title">{t('setupWizard.sourcesTitle')}</h1>
            <p className="etch-auth-screen__desc">{t('setupWizard.sourcesDesc')}</p>

            {/* Documents */}
            <section className="etch-admin-source" data-enabled={sources.documents.enabled}>
              <div className="etch-admin-source__head">
                <div>
                  <h2 className="etch-admin-source__title">{t('setupWizard.sourceDocuments')}</h2>
                  <p className="etch-admin-source__desc">{t('setupWizard.sourceDocumentsDesc')}</p>
                </div>
                <Switch
                  label={t('setupWizard.enable')}
                  checked={sources.documents.enabled}
                  onChange={(e) => updateSource('documents', 'enabled', e.target.checked)}
                />
              </div>

              {deps?.ingestible === false && (
                <Alert tone="warning" title={t('setupWizard.ingestibleMissing')}>
                  <p>{t('setupWizard.ingestibleMissingDesc')}</p>
                  <Button
                    variant="secondary"
                    size="compact"
                    loading={pipInstalling}
                    disabled={pipInstalling}
                    onClick={handlePipInstall}
                  >
                    {t('setupWizard.installIngestible')}
                  </Button>
                </Alert>
              )}
              {deps?.ingestible === true && <Badge tone="success">{t('setupWizard.ingestibleReady')}</Badge>}
              {pipOutput && (
                <pre className="etch-admin-output" tabIndex={0} aria-label={t('setupWizard.installLog')}>
                  {pipOutput}
                </pre>
              )}

              {sources.documents.enabled && (
                <div className="etch-admin-source__fields">
                  <TextField
                    label={t('setupWizard.serviceUrl')}
                    type="url"
                    inputMode="url"
                    value={sources.documents.url}
                    onChange={(e) => updateSource('documents', 'url', e.target.value)}
                  />
                  <div className="etch-row">
                    <Button
                      variant="secondary"
                      size="compact"
                      disabled={testLoading.documents}
                      onClick={() => handleTestConnection('documents')}
                    >
                      {t('setupWizard.testConnection')}
                    </Button>
                    <TestResult type="documents" />
                  </div>
                </div>
              )}
            </section>

            {/* Axiom */}
            <section className="etch-admin-source" data-enabled={sources.axiom.enabled}>
              <div className="etch-admin-source__head">
                <div>
                  <h2 className="etch-admin-source__title">{t('setupWizard.sourceAxiom')}</h2>
                  <p className="etch-admin-source__desc">{t('setupWizard.sourceAxiomDesc')}</p>
                </div>
                <Switch
                  label={t('setupWizard.enable')}
                  checked={sources.axiom.enabled}
                  onChange={(e) => updateSource('axiom', 'enabled', e.target.checked)}
                />
              </div>

              {sources.axiom.enabled && (
                <div className="etch-admin-source__fields">
                  <TextField
                    label={t('setupWizard.accessToken')}
                    type="password"
                    autoComplete="off"
                    placeholder={t('settings.axiomTokenPlaceholder')}
                    value={sources.axiom.token}
                    onChange={(e) => updateSource('axiom', 'token', e.target.value)}
                  />
                  <div className="etch-row">
                    <Button
                      variant="secondary"
                      size="compact"
                      disabled={testLoading.axiom || !sources.axiom.token}
                      onClick={() => handleTestConnection('axiom')}
                    >
                      {t('setupWizard.testConnection')}
                    </Button>
                    <TestResult type="axiom" />
                  </div>
                </div>
              )}
            </section>

            {/* Repositories */}
            <section className="etch-admin-source" data-enabled={sources.repos.enabled}>
              <div className="etch-admin-source__head">
                <div>
                  <h2 className="etch-admin-source__title">{t('setupWizard.sourceRepos')}</h2>
                  <p className="etch-admin-source__desc">{t('setupWizard.sourceReposDesc')}</p>
                </div>
                <Switch
                  label={t('setupWizard.enable')}
                  checked={sources.repos.enabled}
                  onChange={(e) => updateSource('repos', 'enabled', e.target.checked)}
                />
              </div>
              {deps?.ckb === false && (
                <Alert tone="warning" title={t('setupWizard.ckbMissing')}>
                  <code className="etch-mono">npx @tastehub/ckb init &amp;&amp; npx @tastehub/ckb index</code>
                </Alert>
              )}
              {deps?.ckb === true && <Badge tone="success">{t('setupWizard.ckbReady')}</Badge>}
            </section>

            {sourcesError && (
              <Alert tone="danger" title={t('states.error')}>
                {sourcesError}
              </Alert>
            )}

            <Button type="submit" block loading={sourcesLoading} disabled={sourcesLoading}>
              {t('setupWizard.continue')}
            </Button>
          </form>
        )}

        {/* ---- Step 3: summary --------------------------------------------- */}
        {step === 3 && (
          <section className="etch-auth-screen__form" aria-labelledby="setup-done-heading">
            <h1 id="setup-done-heading" className="etch-auth-screen__title">
              {t('setupWizard.doneTitle')}
            </h1>
            <p className="etch-auth-screen__desc">{t('setupWizard.doneDesc')}</p>

            <dl className="etch-admin-kv">
              <div>
                <dt>{t('setupWizard.summaryAdmin')}</dt>
                <dd>{adminForm.username || '—'}</dd>
              </div>
              <div>
                <dt>{t('setupWizard.summarySources')}</dt>
                <dd>{t('setupWizard.summarySourcesValue', { count: enabledCount, total: 3 })}</dd>
              </div>
            </dl>

            <Button block onClick={() => window.location.assign('/')}>
              {t('setupWizard.goToApp')}
            </Button>
          </section>
        )}
      </div>
    </div>
  );
}
